import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Grid,
  IconButton,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getOrganizationUser } from "../../Apis/Organization-User";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import NotAllowed from "../../Exceptions/NotAllowed";
import {
  USER_ADD_ACTIONS,
  USER_DELETE_ACTIONS,
  USER_EDIT_ACTIONS,
  hasCrudOrAllPermission,
} from "../../Utils/organizationPermissions";
import TableDataGeneric from "../../Configurations/TableDataGeneric";



const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "application_user_full_name",
  sortOrder: "asc",
};

const operatorRequiresValue = (operator) => {
  const operatorsWithoutValue = new Set([
    "is_null",
    "is_not_null",
    "empty",
    "not_empty",
  ]);

  return !operatorsWithoutValue.has(operator);
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

const DEFAULT_COLUMNS = [
  { field: "application_user_full_name", label: "Name", visible: true, width: 180, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "application_user_email", label: "Email", visible: true, width: 200, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "application_user_phone", label: "Phone", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "user_type_name", label: "User Type", visible: true, width: 120, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "application_user_role_id_name", label: "Role", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "is_active", label: "Active", visible: true, width: 100, filterable: true, sortable: true, pinned: "none", required: false },
];

const formatUserDisplayName = (name) =>
  String(name ?? "")
    .replace(/\s*\n\s*/g, " ")
    .trim() || "-";

function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { userData } = useAuthStore();
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const org = userData?.organization;
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { Permission, setPermission } = usePermissionDataStore();
  const [showNotAllowed, setShowNotAllowed] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const hasAddPermission = useMemo(
    () => hasCrudOrAllPermission(Permission, USER_ADD_ACTIONS),
    [Permission]
  );

  const hasEditPermission = useMemo(
    () => hasCrudOrAllPermission(Permission, USER_EDIT_ACTIONS),
    [Permission]
  );

  const hasDeletePermission = useMemo(
    () => hasCrudOrAllPermission(Permission, USER_DELETE_ACTIONS),
    [Permission]
  );

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: getApiHeaders({ "Content-Type": "application/json" }),
        });
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_users_grid_${org.organization_id}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (serverCfg?.columns && Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  const handleDeleteConfirm = async () => {
    if (!deleteDialog) return;
    if (!hasDeletePermission) {
      setDeleteDialog(null);
      setShowNotAllowed(true);
      return;
    }

    try {
      // Call the delete API
      await axios.delete(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${deleteDialog}`,  { headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },}
      );

      // Remove user from local state after successful deletion
      setUsers((prev) =>
        prev.filter((u) => u.organization_user_id !== deleteDialog)
      );
      setDeleteDialog(null);
      toast.success("User deleted successfully");
      window.location.reload();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors =
          err.response.data.messages ||
          err.response.data.error ||
          "Validation failed.";
        toast.error(
          Array.isArray(validationErrors)
            ? validationErrors.join(" ")
            : validationErrors
        );
      } else {
        toast.error("Something went wrong while deleting user.");
        setDeleteDialog(null);
      }
    }
  };




  const ToggleStatusTable = async (user, newStatus) => {
    try {
      await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${user.organization_user_id}/toggle`,
        { is_active: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(
        `User status updated to ${newStatus ? "Active" : "Inactive"}`
      );
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.organization_user_id === user.organization_user_id
            ? { ...u, is_active: newStatus ? 1 : 0 }
            : u
        )
      );
    } catch (error) {
      console.error("Failed to update user status", error);
      toast.error("Failed to update user status");
      throw error;
    }
  };



useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500); // 500ms delay

  return () => clearTimeout(handler);
}, [search]);




  const ToggleStatus = async (userId, is_active, id) => {
    console.log("userid", userId);
    console.log("New Status:", is_active);
    try {
      const response = await axios.put(
        `${MAIN_URL}/api/organizations/${org.organization_id}/user/${userId}`,
        { is_active },  { headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },}
      );

      setUsers((prev) =>
        prev.map((u, i) => (i === id ? { ...u, is_active } : u))
      );

      toast.success("User status updated.");
    } catch (error) {
      console.error(error);
      const message =
        error.response?.data?.error ||
        "An error occurred while updating status.";
      toast.error(message);
    }
  };

// useEffect(() => {

//   if (!org?.organization_id) return;
//   setLoading(true);

//   const fetchUsers = async () => {
//     try {
//       const data = await getOrganizationUser(org?.organization_id, search, filterStatus);
//       console.log("data is ", data)
//       setFilteredUsers(data.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };
//   fetchUsers();
// }, [org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;
//   const fetchUsers = async () => {
//     try {
   
//        const data = await getOrganizationUser(org?.organization_id);
//       console.log("saysgugugugugugs", data)
//       setFilteredUsers(data?.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     }
//   };
//   fetchUsers();
// }, [search, filterStatus, org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;

//   const fetchUsers = async () => {
//     try {
//       setLoading(true);

//       const data = await getOrganizationUser(
//         org?.organization_id,
//         debouncedSearch,     // ⬅ IMPORTANT
//         filterStatus
//       );

//       setFilteredUsers(data?.users || []);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   fetchUsers();
// }, [org?.organization_id, debouncedSearch, filterStatus]);






  useEffect(() => {
    if (!org?.organization_id) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const page = Number.parseInt(searchParams.get("page"), 10) || 1;
    const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");

        const requestBody = {
          filters,
          page,
          pageSize,
          sort: {
            field:
              searchParams.get("sortBy") || "application_user_full_name",
            direction: searchParams.get("sortOrder") || "asc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/user/filtered`,
          {
            method: "POST",
            headers: getApiHeaders(),
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            const pag = result.pagination;
            const limit = pag?.pageSize ?? pag?.limit ?? pageSize;
            const total = pag?.total ?? 0;
            const totalPages = Math.max(
              1,
              pag?.totalPages ?? (total > 0 ? Math.ceil(total / limit) : 1)
            );

            setPaginationData({
              page: pag?.page ?? page,
              pageSize: limit,
              total,
              totalPages,
              hasNext: Boolean(pag?.hasNext ?? page < totalPages),
              hasPrev: Boolean(pag?.hasPrev ?? page > 1),
            });
          } else {
            data = [];
            setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const mapped = (data || []).map((item) => ({
          ...item,
          id: item.organization_user_id,
        }));

        setUsers(mapped);
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("Failed to fetch users:", err);
        toast.error("Failed to fetch users");
        setUsers([]);
        setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams.toString()]);

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentField =
          prev.get("sortBy") || "application_user_full_name";
        const currentDirection = prev.get("sortOrder") || "asc";

        // Only reset to page 1 if the sort field/direction actually changed.
        if (currentField !== field || currentDirection !== direction) {
          next.set("sortBy", field);
          next.set("sortOrder", direction);
          next.set("page", "1");
        }

        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filtersOrUpdates) => {
      setSearchParams((prev) => {
        const prevParams = new URLSearchParams(prev);
        const next = new URLSearchParams(prev);

        const buildFilterSignature = (params) =>
          [...params.keys()]
            .filter((k) => k.startsWith("filter_") || k === "search")
            .sort()
            .map((k) => `${k}=${params.get(k) ?? ""}`)
            .join("&");

        const prevSignature = buildFilterSignature(prevParams);

        if (Array.isArray(filtersOrUpdates)) {
          [...next.keys()]
            .filter((k) => k.startsWith("filter_"))
            .forEach((k) => next.delete(k));
          filtersOrUpdates.forEach((f) => {
            if (f?.field != null) {
              next.set(`filter_${f.field}`, f.value ?? "");
              if (f.operator) next.set(`filter_${f.field}_operator`, f.operator);
            }
          });
        } else if (
          filtersOrUpdates &&
          typeof filtersOrUpdates === "object" &&
          filtersOrUpdates.searchQuery !== undefined
        ) {
          if (String(filtersOrUpdates.searchQuery).trim() !== "") {
            next.set("search", filtersOrUpdates.searchQuery.trim());
          } else {
            next.delete("search");
          }
        }

        const nextSignature = buildFilterSignature(next);

        // Only reset to page 1 if filters or search actually changed.
        if (prevSignature !== nextSignature) {
          next.set("page", "1");
        }

        return next;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (page) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(page));
        if (!next.has("pageSize")) next.set("pageSize", "10");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Table data: use `users` (from POST /user/filtered). Support both flat API shape and legacy nested shape.
  const tableRows = useMemo(
    () =>
      users.map((user) => ({
        ...user,
        organization_user_id: user.organization_user_id,
        application_user_full_name: formatUserDisplayName(
          user.user_name ??
            user.full_name ??
            user?.applicationuser?.[0]?.full_name
        ),
        application_user_email:
          user.email ?? user?.applicationuser?.[0]?.email ?? "-",
        application_user_phone:
          user.phone ??
          user?.applicationuser?.[0]?.phone_number ??
          "-",
        application_user_role_id_name:
          user.user_role ??
          user.application_user_role ??
          user?.role_assignment?.application_user_role?.user_role_name ??
          "-",
        user_type_name:
          user.user_type ??
          user.user_type_name ??
          user?.user_types?.user_type_name ??
          "-",
        __is_active_for_toggle:
          user?.is_active == 1 ||
          user?.is_active === true ||
          user?.is_active === "1",
        is_active:
          user?.is_active == 1 ||
          user?.is_active === true ||
          user?.is_active === "1"
            ? "Active"
            : "Inactive",
      })),
    [users]
  );

  const handleEdit = (row) => {
    navigate(`/users/edit/${row.organization_user_id}`);
  };

  const deleteUser = (id) => {
    // Use existing dialog flow
    setDeleteDialog(id);
  };

if(showNotAllowed)
{
  return <NotAllowed/>
}
  return (
    <Box p={4} minHeight="100%">
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5" fontWeight={600}>
              Users Management
            </Typography>
            {hasAddPermission && (
              <Button
                variant="contained"
                onClick={() => navigate(`/users/add`)}
                sx={{ borderRadius: 2 }}
              >
                Add User
              </Button>
            )}
          </Box>

          {/* Generic, configurable table */}
          {!loadingConfig && (
            <TableDataGeneric
              tableName="Users"
              primaryKey="organization_user_id"
              heading="Users"
              data={tableRows}
              sortname="organization_user_id"
              Route="/users"
              DeleteFunc={deleteUser}
              EditFunc={hasEditPermission ? handleEdit : undefined}
              showActions={true}
              token={localStorage.getItem("token")}
              configss={configColumns}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
              onSortChange={handleSortChange}
              onFilterChange={handleFilterChange}
              onPageChange={handlePageChange}
              filterModule="organization_users"
              edit_delete_action={["USER_EDIT", "USER_DELETE"]}
              toggleActiveFn={
                hasEditPermission
                  ? (row, nextActive) => ToggleStatusTable(row, nextActive)
                  : undefined
              }
              {...(tableConfig && { config: tableConfig })}
            />
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this user?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
              <Button color="error" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default Users;

